import type { ReactNode } from "react";
import { Alert, Box, Typography } from "@dealer-center/core-components";
import { LoadingIndicator } from "@dealer-center/core-custom-components";
import { Locale } from "@dealer-center/ddc-core-utils";
import styled from "styled-components";
import { SPACE_SECTION } from "./layoutTokens";
import { useAnnounceStatus } from "./StatusRegion/userInfoStatusContext";

/**
 * Every panel's inset, applied to all four states so a spinner, an error, an empty message, and real
 * content all sit on the same left edge as the identity row above.
 *
 * It lives **here** rather than in each tab because Interstate's `Tabs` renders the `role="tabpanel"`
 * element itself, so a tab cannot pad its own panel from outside — and padding only the content
 * branch would let the loading and failed states sit flush against the card's edge.
 */
const PANEL_PADDING = { padding: SPACE_SECTION };

const StyledLoadingBox = styled(Box)`
  align-items: center;
  display: flex;
  height: 100%;
  justify-content: center;
`;

/**
 * The three states FR-048 requires every tab to distinguish, as one wrapper each tab composes.
 *
 * Default copy comes from `labels.json` (`analytics.LOADING`, `TAB_EMPTY`, `TAB_FAILED`) so all three
 * locales resolve through Label Services and no locale renders a raw key — **Configuration
 * Principle VI** (Localized Labels — No Hardcoded UI Text), FR-049, SC-009. A tab needing more
 * specific wording — Feature Access's FR-035 empty state, Login History's FR-040 one — overrides
 * it, passing a localized string from `labels.json`, never a literal.
 */
export interface TabStateProps {
  /** The query is pending. Takes precedence over every other state. */
  isLoading: boolean;
  /** The query failed. Renders the failure message and never partial data. */
  isError: boolean;
  /**
   * The query succeeded with nothing to show. **Not** an error (FR-040). Callers must compute
   * this from the payload, not from falsiness of the payload — `totalPages: 0` and
   * `permissions: []` are legitimate successes.
   */
  isEmpty: boolean;
  /**
   * Names the action for the New Relic timing the spinner reports. Not user-visible, so it is a
   * plain string rather than a label key.
   */
  loadingActionName: string;
  /** Optional per-tab overrides. Each MUST be a localized string, never a literal. */
  loadingMessage?: string;
  emptyMessage?: string;
  failedMessage?: string;
  /** Rendered only when the query has succeeded with content. */
  children: ReactNode;
  testId?: string;
  /**
   * Overrides what this tab announces in the shell's live region (T069).
   *
   * Without it, the tab announces whatever it *displays* — which is the right default, since the
   * visible message and the spoken one should not drift apart. Pass it only when they must differ:
   * Login History does, because its ~20s latency deserves "this can take up to a minute" out loud
   * and it is the one tab with something worth saying on **success** ("loaded"), where the other
   * three simply reveal their content and stay quiet.
   *
   * An empty string announces nothing.
   */
  announcement?: string;
}

/**
 * Precedence is loading → error → empty → content, and it is deliberate:
 *
 * - **Loading first**, because a tab that renders its empty state while the query is still in
 *   flight reads as "you have nothing here" — the Edge Case that forbids a momentarily empty tab.
 * - **Error before empty**, because a failed query has no payload, so "empty" would be an artefact
 *   of the failure rather than a fact about the user's data.
 *
 * A failure here is scoped to this tab. The four queries share no state, so a sibling tab stays
 * usable (SC-010).
 */
export const TabState = ({
  isLoading,
  isError,
  isEmpty,
  loadingMessage,
  loadingActionName,
  emptyMessage,
  failedMessage,
  children,
  testId,
  announcement
}: TabStateProps) => {
  const loadingText =
    loadingMessage ?? (Locale.getText("analytics.LOADING") as string);
  const failedText = failedMessage ?? (Locale.getText("TAB_FAILED") as string);
  const emptyText = emptyMessage ?? (Locale.getText("TAB_EMPTY") as string);

  /*
   * T069. Announced through the shell's region, and called **before** the early returns below — a
   * hook cannot sit behind one, so the message is computed from props rather than from which branch
   * renders. The content state announces nothing by default: the content is right there to read, and
   * a spoken "loaded" on every tab would be noise.
   */
  useAnnounceStatus(
    announcement ??
      (isLoading
        ? loadingText
        : isError
          ? failedText
          : isEmpty
            ? emptyText
            : "")
  );

  if (isLoading) {
    return (
      <StyledLoadingBox
        data-testid={testId ? `${testId}-loading` : "tab-state-loading"}
        sx={PANEL_PADDING}
      >
        <LoadingIndicator actionName={loadingActionName} text={loadingText} />
      </StyledLoadingBox>
    );
  }

  if (isError) {
    return (
      <Box
        data-testid={testId ? `${testId}-failed` : "tab-state-failed"}
        sx={PANEL_PADDING}
      >
        <Alert role={"alert"} type={"error"}>
          {failedText}
        </Alert>
      </Box>
    );
  }

  if (isEmpty) {
    return (
      <Box
        data-testid={testId ? `${testId}-empty` : "tab-state-empty"}
        sx={PANEL_PADDING}
      >
        <Typography tag={"p"} variant={"body-md"}>
          {emptyText}
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      data-testid={testId ? `${testId}-content` : "tab-state-content"}
      sx={PANEL_PADDING}
    >
      {children}
    </Box>
  );
};
